import { GlobalStore } from '@roc/feature-app-core';
import {
  DateFormat,
  formatDate,
  generateUUID,
  GENERIC_ERROR_MESSAGE,
  normalizeState,
  PURCHASE,
} from '@roc/feature-utils';
import { action, computed, flow, makeObservable, observable } from 'mobx';
import { OneToolService } from '../../services/oneToolService';
import { PropertyFormStore } from './propertyFormStore';

export abstract class PropertiesStore {
  protected globalStore: GlobalStore;
  protected loanSubType: string;
  private oneToolService: OneToolService;

  public propertyFormStores: PropertyFormStore[];
  public uploadedLicenses = {};

  public key = generateUUID();

  abstract createPropertyForm(): PropertyFormStore;

  constructor(globalStore, loanSubType) {
    this.globalStore = globalStore;
    this.loanSubType = loanSubType;
    this.oneToolService = new OneToolService();
    makeObservable(this, {
      reset: action,
      propertyFormStores: observable,
      createPropertyForm: action,
      addNewProperty: action,
      removeProperty: action,
      removePropertyInAccordion: action,
      validateAllProperties: action,
      turnOffPropertiesEditMode: action,
      getProperties: action,
      monthlyRent: computed,
      asIsValue: computed,
      annualInsurance: computed,
      annualHoa: computed,
      annualTaxes: computed,
      annualExpenses: computed,
      loadProperties: action,
      uploadedLicenses: observable,
      uploadStateLicense: flow,
      removeStateLicense: action,
    });
  }

  reset() {
    this.propertyFormStores = [];
    this.addNewProperty();
  }

  addNewProperty() {
    this.propertyFormStores.push(this.createPropertyForm());
  }

  removeProperty(propertyKey: string) {
    this.propertyFormStores = this.propertyFormStores.filter(
      property => property.form.meta.key !== propertyKey
    );
  }

  removePropertyInAccordion(propertyKey: string) {
    this.propertyFormStores = this.propertyFormStores.filter(
      property => property.key !== propertyKey
    );
  }

  validateAllProperties() {
    let isValid = true;

    this.propertyFormStores.forEach(store => {
      store.validateAndCleanupProperty();
      isValid = isValid && store.form.meta.isValid;
      store.setEditMode(!isValid);
    });

    if (!isValid) {
      this.globalStore.notificationStore.showWarningNotification({
        message: 'Please complete all the required fields for the properties.',
      });
      return false;
    }

    return true;
  }

  turnOffPropertiesEditMode() {
    this.propertyFormStores.forEach(propertyFormStore => {
      propertyFormStore.form.meta.editMode = false;
    });
  }

  getProperties() {
    return this.propertyFormStores?.map(form => form.getProperty());
  }

  loadProperties(quoteData) {
    const properties = quoteData.properties;
    if (properties?.length > 0) {
      this.propertyFormStores = properties?.map(property => {
        const form = this.createPropertyForm();
        form.loadProperty({
          ...property,
          cashOut: property.seekingCashOut
        });
        form.setEditMode(false);
        return form;
      });
    }

    this.uploadedLicenses = quoteData.uiState?.uploadedLicenses || {};
  }

  *uploadStateLicense(file: File, state: string) {
    try {
      const fileType = new Blob([`State License - ${normalizeState(state)}`], {
        type: 'text/plain',
      });
      const companyId = new Blob([this.globalStore.selectedCompanyId], {
        type: 'text/plain',
      });
      const formData = new FormData();
      formData.append('file', file);
      formData.set('fileType', fileType);
      formData.set('companyId', companyId);
      yield this.oneToolService.uploadLicense(formData);

      this.uploadedLicenses = { ...this.uploadedLicenses, [state]: file.name };
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  removeStateLicense(state) {
    delete this.uploadedLicenses[state];
  }

  get monthlyRent(): number {
    const properties = this.getProperties();
    const isSingleProperty = properties.length === 1;

    if (isSingleProperty) {
      return properties[0].monthlyMarketRent || 0;
    }

    return properties.reduce((sum, property) => {
      return sum + (property.monthlyGrossRent || 0);
    }, 0);
  }

  get asIsValue(): number {
    const properties = this.getProperties();
    const isSingleProperty = properties.length === 1;

    if (isSingleProperty) {
      const property = properties[0];
      const valueBasedOnLoanType = property.currentMarketValue;

      return property.loanPurpose === PURCHASE
        ? Math.min(valueBasedOnLoanType, property.purchasePrice)
        : valueBasedOnLoanType;
    }

    //TODO verify that the logic is correct when there are multiple properties
    const totalValue = properties.reduce((sum, property) => {
      const value = property.currentMarketValue || 0;
      return sum + value;
    }, 0);

    return totalValue;
  }

  get annualTaxes(): number {
    const properties = this.getProperties();
    const isSingleProperty = properties.length === 1;

    if (isSingleProperty) {
      return Number(properties[0].annualTaxes) || 0;
    }

    return properties.reduce((sum, property) => {
      return sum + (Number(property.annualTaxes) || 0);
    }, 0);
  }

  get annualInsurance(): number {
    const properties = this.getProperties();
    const isSingleProperty = properties.length === 1;

    if (isSingleProperty) {
      return Number(properties[0].annualInsurance) || 0;
    }

    return properties.reduce((sum, property) => {
      return sum + (Number(property.annualInsurance) || 0);
    }, 0);
  }

  get annualHoa(): number {
    const properties = this.getProperties();
    const isSingleProperty = properties.length === 1;

    if (isSingleProperty) {
      return Number(properties[0].annualHOAProvidedAtOrigination) || 0;
    }

    return properties.reduce((sum, property) => {
      return sum + (Number(property.annualHOAProvidedAtOrigination) || 0);
    }, 0);
  }

  get annualExpenses(): number {
    return this.annualTaxes + this.annualInsurance + this.annualHoa;
  }

}
